import {
  useGetApiV1ExamDisciplineDropdownFiltered,
  useGetApiV1ExamDisciplineSingle,
} from '@/shared/api/generated';
import { useDebouncedFilter } from '@/shared/ui/molecules/LookupAutocomplete';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

/**
 * Busca server-side de disciplinas para o autocomplete da questão.
 *
 * @param filter - Texto digitado (mínimo 3 caracteres, com debounce de 1s)
 * @returns Opções cruas do dropdown e o estado de sucesso da query
 *
 * @example
 * ```tsx
 * const { options, isQuerySuccess } = useDisciplineSearch(search);
 * ```
 */
export function useDisciplineSearch(filter: string) {
  const { debouncedFilter, isReady } = useDebouncedFilter(filter);
  const query = useGetApiV1ExamDisciplineDropdownFiltered(
    isReady ? { filter: debouncedFilter } : undefined,
    { query: { enabled: isReady } },
  );

  return {
    options: unwrapPagedResponse(query.data),
    isQuerySuccess: query.isSuccess,
  } as const;
}

/**
 * Resolve o nome de uma disciplina a partir do seu ID.
 *
 * Necessário na edição: o detalhe da questão devolve apenas `disciplineId`, e o
 * dropdown de busca só responde a partir de 3 caracteres digitados — sem esta
 * consulta o campo abriria vazio mesmo com disciplina salva.
 *
 * @param disciplineId - ID da disciplina salva na questão; `null` desabilita a query
 * @returns Nome da disciplina, ou string vazia enquanto não resolvido
 *
 * @example
 * ```tsx
 * const disciplineName = useDisciplineName(question.disciplineId);
 * ```
 */
export function useDisciplineName(disciplineId: number | null): string {
  const query = useGetApiV1ExamDisciplineSingle(
    disciplineId !== null ? { id: disciplineId } : undefined,
    { query: { enabled: disciplineId !== null } },
  );

  return unwrapPagedResponse(query.data)[0]?.value ?? '';
}
